/* Core & libraries */
import React, { useState } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import { ProgressBar } from "react-bootstrap";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";

/* Helpers, constants, custom methods, internal libraries */
import { useHasPermission } from "../../hooks/useHasPermission";
import { useDiscountService } from "services/discountService";

/* Components */
import UCButton from "../../components/buttons";
import { ReactComponent as AwardIcon } from "../../resources/images/aa-discounts-award.svg";
import { ReactComponent as VerticalDotsSvg } from "../../resources/images/dots-vertical.svg";
import { ReactComponent as RefreshIcon } from "../../resources/images/refresh-icon.svg";
import DeclineModal from "components/modals/declineModal";
import BenefitsCard from "./benefitsCard";

/* Styles */
import styles from "./styles";

const DiscountsContent = ({ data, onSuccess, beResponse }) => {
  const theme = useTheme();
  const classes = styles({ theme });

  const { checkPermission } = useHasPermission();
  const { postDiscountTerms } = useDiscountService();

  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const currentYear = new Date().getFullYear();

  const getTierMessage = (tierStatus, type) => {
    const isStatus = type === "status";

    const messages = {
      QUALIFIED: isStatus
        ? "discounts.content.qualifiedTierStatus"
        : "discounts.content.qualifiedTierDate",
      ACCEPTED: isStatus
        ? "discounts.content.acceptedTierStatus"
        : data?.currentTierManaged
          ? "discounts.content.acceptedManagedTierDate"
          : "discounts.content.acceptedTierDate",
      REQUALIFY: isStatus
        ? "discounts.content.requalifyTierStatus"
        : "discounts.content.requalifyTierDate",
      ADMINDECLINED: isStatus
        ? "discounts.content.noTierStatus"
        : "discounts.content.noTierDate",
      EMPTY: isStatus
        ? "discounts.content.noTierStatus"
        : "discounts.content.noTierDate",
      FALLBACK: isStatus
        ? "discounts.content.tierStatusFallback"
        : "discounts.content.tierDateFallback",
    };

    const messageId =
      tierStatus === false || tierStatus === "" || tierStatus == null
        ? messages.EMPTY
        : messages[tierStatus] || messages.FALLBACK;

    return (
      <FormattedMessage
        id={messageId}
        values={{
          [isStatus ? "tierToUnlock" : "displayedTierDate"]:
            data && data[isStatus ? "tierToUnlock" : "displayedTierDate"],
          currentYear,
          difference:
            data?.valueToUnlock - data?.currentSpend > 0
              ? new Intl.NumberFormat("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(data?.valueToUnlock - data?.currentSpend)
              : "",
        }}
      />
    );
  };

  const handleOptOut = () => {
    document.getElementById("root").click();
    setShowDeclineModal(true);
  };

  const handleDecline = async () => {
    const success = await postDiscountTerms({
      action: "DECLINE",
      tierCode: data?.currentTierCode || "",
      discountCode: data?.currentDiscountCode || "",
    });
    if (success) {
      setShowDeclineModal(false);
      onSuccess();
    }
  };

  const renderPopover = () => {
    return (
      <Popover className={classes.popoverStyle}>
        <div className={classes.popoverDiv}>
          <div className={classes.popoverItem} onClick={handleOptOut}>
            <FormattedMessage id="discounts.optOutButton" />
          </div>
        </div>
      </Popover>
    );
  };

  return (
    <div className={classes.contentContainer}>
      {beResponse ? (
        <div className={classes.tierCard}>
          <div className={classes.tierTypeWrapper}>
            <div
              className={
                data?.tierStatus === "QUALIFIED" ||
                data?.tierStatus === "ACCEPTED"
                  ? classes.tierTypeIconActive
                  : classes.tierTypeIcon
              }
            >
              <AwardIcon />
            </div>
            <div>
              <h6>
                <FormattedMessage
                  id="discounts.content.tierTitle"
                  values={{
                    title: data?.currentTier
                      ? `AAdvantage Business ${data?.currentTier}℠`
                      : `AAdvantage Business℠ ${data?.noTierTitle}`,
                  }}
                />
              </h6>
            </div>
            {checkPermission("F_DISCOUNTTIER_MANAGETIERLEVEL") &&
              beResponse?.currentTier?.allowOptOut && (
                <OverlayTrigger
                  trigger="click"
                  rootClose
                  placement="left"
                  overlay={renderPopover()}
                  onEnter={() => setOverlayOpen(true)}
                  onExit={() => setOverlayOpen(false)}
                >
                  <div
                    className={`${classes.dotsStyle} ${overlayOpen ? classes.dotsOpenStyle : ""}`}
                  >
                    <VerticalDotsSvg />
                  </div>
                </OverlayTrigger>
              )}
          </div>
          <ProgressBar
            now={
              !data?.currentTierManaged
                ? Math.min(
                    (Number(data?.currentSpend) / Number(data?.valueToUnlock)) *
                      100,
                    100
                  )
                : 100
            }
            className={
              data?.tierStatus === "QUALIFIED" ||
              data?.tierStatus === "ACCEPTED"
                ? classes.progressBarActive
                : classes.progressBar
            }
          />
          <div className={classes.infoContainer}>
            <div className={classes.infoWrapper}>
              <h6>{getTierMessage(data?.tierStatus, "status")}</h6>
              <span>{getTierMessage(data?.tierStatus, "date")}</span>
            </div>
            <div className={classes.infoWrapper}>
              <h6>
                {!data?.currentTierManaged ? (
                  <FormattedMessage
                    id="discounts.content.valueSpent"
                    values={{
                      current: new Intl.NumberFormat("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(data?.currentSpend),
                      required: new Intl.NumberFormat("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(data?.valueToUnlock),
                    }}
                  />
                ) : (
                  `$${new Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(data?.currentSpend)}`
                )}
              </h6>

              <span>
                {!data?.currentTierManaged ? (
                  <FormattedMessage
                    id="discounts.totalSpend"
                    values={{ currentYear }}
                  />
                ) : (
                  <FormattedMessage id="discounts.totalSpendManaged" />
                )}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className={classes.tierCard}>
          <div className={classes.tierUnavailableWrapper}>
            <h6>
              <FormattedMessage id="discounts.tierUnavailableTitle" />
            </h6>
            <p>
              <FormattedMessage id="discounts.tierUnavailableDescription" />
            </p>
            <UCButton
              className={classes.retryBtn}
              type="default"
              onClick={() => onSuccess()}
              icon={<RefreshIcon />}
              text={<FormattedMessage id="discounts.tierUnavailableRetry" />}
            />
          </div>
        </div>
      )}
      <BenefitsCard data={data} beResponse={beResponse} onSuccess={onSuccess} />
      <DeclineModal
        showModal={showDeclineModal}
        setShowModal={setShowDeclineModal}
        submitCallback={handleDecline}
        tierName={data?.currentTier}
      />
    </div>
  );
};

export default DiscountsContent;
